import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, LogIn, KeyRound, Pencil } from 'lucide-react';

/**
 * Iter 212 — Shared inline "email is already in use" block.
 *
 * Designed to appear under the email input on ANY signup form so the
 * applicant discovers the conflict immediately — not after completing
 * the rest of the form. Surfaces three remediation actions:
 *
 *   1. Sign in           → `/signin`
 *   2. Forgot password   → `/forgot-password`
 *   3. Change email      → fires `onChangeEmail` which the parent uses
 *                          to clear/focus the email input.
 *
 * Props:
 *   reason          string | undefined  — from backend ('user_exists'
 *                                          | 'business_listing_exists').
 *                                          Optional; used to tweak copy.
 *   onChangeEmail   () => void           — called when "Change email"
 *                                          is clicked. Parent should
 *                                          clear the field and focus it.
 *   testIdPrefix    string               — defaults to 'email-taken'.
 */
const EmailTakenNotice = ({ reason, onChangeEmail, testIdPrefix = 'email-taken' }) => {
  const navigate = useNavigate();
  const isSeeded = reason === 'business_listing_exists';
  return (
    <div
      className="mt-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm"
      data-testid={`${testIdPrefix}-block`}
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-rose-800 font-semibold leading-snug">
            This email address is already registered.
          </p>
          <p className="text-rose-700 text-xs mt-0.5 leading-relaxed">
            {isSeeded
              ? 'A listing using this email already exists in our directory. Please sign in, recover your password or use a different email address.'
              : 'Please sign in or use a different email address.'}
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate('/signin')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 transition-colors"
          data-testid={`${testIdPrefix}-signin-btn`}
        >
          <LogIn className="h-3.5 w-3.5" /> Sign in
        </button>
        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-100 transition-colors"
          data-testid={`${testIdPrefix}-forgot-btn`}
        >
          <KeyRound className="h-3.5 w-3.5" /> Forgot password
        </button>
        {onChangeEmail && (
          <button
            type="button"
            onClick={onChangeEmail}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-100 transition-colors"
            data-testid={`${testIdPrefix}-change-btn`}
          >
            <Pencil className="h-3.5 w-3.5" /> Change email
          </button>
        )}
      </div>
    </div>
  );
};

export default EmailTakenNotice;
